import { all, takeEvery, select, put } from 'redux-saga/effects';
import { decamelizeKeys } from 'humps';
import { defaultTo, isEmpty, pipe, addIndex, map, always, cond, includes, equals, either } from 'ramda';
import { AnalyticsActions, AnalyticsTypes } from './analytics.redux';
import {
  GTMBookingStep,
  GTMEvents,
  GTMList,
  GTMOriginalEvents,
  IntercomBookingStep,
  IntercomEvents,
} from './analytics.constants';
import * as GTM from '../../shared/services/gtm';
import * as Intercom from '../../shared/services/intercom';
import { BookingCurrency } from '../../shared/services/booking/currency';
import { selectMarkets, selectExperiencesRegions, selectVendors, selectExperienceById } from '../contentRegistry';
import { selectFilteredExperiences } from '../filtered';
import { selectCurrentMarketExperiences } from '../market';
import { ROUTES_BASE } from '../../routes/routes';
import { selectBookingDate, selectBookingPrice, selectBookingTickets, selectSelectedTicketsCount } from '../booking';
import { selectCurrentExperienceId, selectCurrentExperienceMarketData } from '../experience';
import { selectUserId } from '../auth';
import {
  getBrand,
  getCategory,
  getCheckoutProducts,
  getExperiencePropertiesForGTM,
  getExperiencePropertiesForIntercom,
  getIntercomPriceObject,
  getNameById,
  sliceExperiences,
  updateExperiencesPosition,
} from './analytics.utils';
import { TICKET_TYPES } from '../booking/booking.constants';

function* logGTMOriginalEvent(event, params) {
  const userId = yield select(selectUserId);
  GTM.logEvent(event, { userId, ...params });
}

function* handleViewedExperiencesGTMEvent({ experiences, list }) {
  const markets = yield select(selectMarkets);
  const vendors = yield select(selectVendors);
  const experiencesRegions = yield select(selectExperiencesRegions);

  const impressions = pipe(
    defaultTo([]),
    addIndex(map)(({ id, name, vendor, market: marketId, position }, index) => {
      const market = getNameById(marketId)(markets);
      const region = getNameById(id)(experiencesRegions);
      const brand = getBrand(vendor, vendors);
      const category = getCategory(market, region);

      return {
        id,
        name,
        category,
        list,
        brand,
        position: position || index + 1,
      };
    })
  )(experiences);

  if (isEmpty(impressions)) {
    return;
  }

  yield logGTMOriginalEvent(GTMOriginalEvents.EcommerceImpressionView, {
    ecommerce: {
      currencyCode: BookingCurrency.USD,
      impressions,
    },
  });
}

function* handleViewExperiencePageGTMEvent({ experience }) {
  const properties = yield getExperiencePropertiesForGTM(experience);

  yield logGTMOriginalEvent(GTMOriginalEvents.EcommerceDetail, {
    ecommerce: {
      detail: {
        products: [properties],
      },
    },
  });
}

function* handleCheckoutStepInit(step) {
  const experienceId = yield select(selectCurrentExperienceId);
  const experience = yield select(selectExperienceById(experienceId));
  const properties = yield getExperiencePropertiesForGTM(experience);

  yield logGTMOriginalEvent(GTMOriginalEvents.EcommerceCheckout, {
    ecommerce: {
      checkout: {
        actionField: {
          step,
        },
        products: [{ ...properties, quantity: 1 }],
      },
    },
  });
}

function* handleCheckoutStep1() {
  const date = yield select(selectBookingDate);

  yield logGTMOriginalEvent(GTMOriginalEvents.EcommerceCheckout, {
    ecommerce: {
      //eslint-disable-next-line
      checkout_option: {
        actionField: {
          step: GTMBookingStep.DateAndTime,
          option: date,
        },
      },
    },
  });
}

function* handleCheckoutStep2({ action }) {
  yield logGTMOriginalEvent(GTMOriginalEvents.EcommerceCheckout, {
    ecommerce: {
      //eslint-disable-next-line
      checkout_option: {
        actionField: {
          step: GTMBookingStep.Authenticate,
          option: action,
        },
      },
    },
  });
}

function* handleCheckoutStep3() {
  const selectedTicketsCount = yield select(selectSelectedTicketsCount);
  yield logGTMOriginalEvent(GTMOriginalEvents.EcommerceCheckout, {
    ecommerce: {
      //eslint-disable-next-line
      checkout_option: {
        actionField: {
          step: GTMBookingStep.Guests,
          option: selectedTicketsCount,
        },
      },
    },
  });
}

function* handleCheckoutStep4Init() {
  const products = yield getCheckoutProducts();

  yield logGTMOriginalEvent(GTMOriginalEvents.EcommerceCheckout, {
    ecommerce: {
      checkout: {
        actionField: {
          step: GTMBookingStep.Payment,
        },
        products,
      },
    },
  });
}

function* handlePurchase({ id, bookingData }) {
  const products = yield getCheckoutProducts();
  const revenue = yield select(selectBookingPrice);

  yield logGTMOriginalEvent(GTMOriginalEvents.EcommercePurchase, {
    ecommerce: {
      purchase: {
        actionField: {
          id,
          revenue,
        },
        products,
      },
    },
  });

  const bookingValue = yield select(selectBookingPrice);
  const { slug: bookingMarket } = yield select(selectCurrentExperienceMarketData);

  yield GTM.logBookingPurchaseComplete({
    tickets: bookingData.tickets,
    bookingCurrency: BookingCurrency.USD,
    bookingMarket,
    bookingValue,
  });
}

function* handleClickOnExperienceGTMEvent({ experience, location, position }) {
  const properties = yield getExperiencePropertiesForGTM(experience);
  const { pathname } = location;

  const list = cond([
    [either(includes(ROUTES_BASE.experiences), includes(ROUTES_BASE.experience)), always(GTMList.Related)],
    [includes(ROUTES_BASE.market), always(GTMList.Market)],
    [includes(ROUTES_BASE.filtered), always(GTMList.Filtered)],
    [includes(ROUTES_BASE.kiosk), always(GTMList.Kiosk)],
    [equals(ROUTES_BASE.home), always(GTMList.Home)],
  ])(pathname);

  if (!list || !properties) {
    return;
  }

  yield logGTMOriginalEvent(GTMOriginalEvents.EcommerceImpressionClick, {
    ecommerce: {
      click: {
        actionField: {
          list,
        },
        products: [{ ...properties, position }],
      },
    },
  });
}

function* handleBaseEvent(event) {
  yield logGTMOriginalEvent(event);
}

function* handleAttemptsEvent(event, { attempts: authAttempts }) {
  yield logGTMOriginalEvent(event, { authAttempts });
}

const gtmEventHandlersMap = {
  [GTMEvents.ViewedExperiences]: handleViewedExperiencesGTMEvent,
  [GTMEvents.ViewExperiencePage]: handleViewExperiencePageGTMEvent,
  [GTMEvents.ClickOnExperience]: handleClickOnExperienceGTMEvent,
  [GTMEvents.CheckoutStep1Init]: () => handleCheckoutStepInit(GTMBookingStep.DateAndTime),
  [GTMEvents.CheckoutStep1]: handleCheckoutStep1,
  [GTMEvents.CheckoutStep2Init]: () => handleCheckoutStepInit(GTMBookingStep.Authenticate),
  [GTMEvents.CheckoutStep2]: handleCheckoutStep2,
  [GTMEvents.CheckoutStep3Init]: () => handleCheckoutStepInit(GTMBookingStep.Guests),
  [GTMEvents.CheckoutStep3]: handleCheckoutStep3,
  [GTMEvents.CheckoutStep4Init]: handleCheckoutStep4Init,
  [GTMEvents.Purchase]: handlePurchase,
  [GTMEvents.AuthOpen]: () => handleBaseEvent(GTMOriginalEvents.AuthOpen),
  [GTMEvents.AuthClose]: () => handleBaseEvent(GTMOriginalEvents.AuthClose),
  [GTMEvents.AuthComplete]: () => handleBaseEvent(GTMOriginalEvents.AuthComplete),
  [GTMEvents.AuthSignupSuccess]: params => handleAttemptsEvent(GTMOriginalEvents.AuthSignupSuccess, params),
  [GTMEvents.AuthSignupFail]: params => handleAttemptsEvent(GTMOriginalEvents.AuthSignupFail, params),
  [GTMEvents.AuthLoginSuccess]: params => handleAttemptsEvent(GTMOriginalEvents.AuthLoginSuccess, params),
  [GTMEvents.AuthLoginFail]: params => handleAttemptsEvent(GTMOriginalEvents.AuthLoginFail, params),
};

function* logGTMEvent({ event, params }) {
  const handler = gtmEventHandlersMap[event];

  if (handler) {
    yield handler(params);
  }
}

function* logFilteredViewedExperiences({ startIndex, endIndex }) {
  const filteredExperiences = yield select(selectFilteredExperiences);
  const sliced = sliceExperiences(startIndex, endIndex)(filteredExperiences);
  const experiences = updateExperiencesPosition(startIndex)(sliced);

  yield put(AnalyticsActions.logGTMEvent(GTMEvents.ViewedExperiences, { experiences, list: GTMList.Filtered }));
}

function* logMarketViewedExperiences({ startIndex, endIndex }) {
  const marketExperiences = yield select(selectCurrentMarketExperiences);
  const sliced = sliceExperiences(startIndex, endIndex)(marketExperiences);
  const experiences = updateExperiencesPosition(startIndex)(sliced);

  yield put(AnalyticsActions.logGTMEvent(GTMEvents.ViewedExperiences, { experiences, list: GTMList.Market }));
}

function* logKioskViewedExperiences({ experiences }) {
  yield put(AnalyticsActions.logGTMEvent(GTMEvents.ViewedExperiences, { experiences, list: GTMList.Kiosk }));
}

function* handleIntercomExperienceViewEvent({ experience }) {
  const properties = yield getExperiencePropertiesForIntercom(experience);
  Intercom.logEvent(IntercomEvents.ExperienceView, properties);
}

function* handleCheckoutStepIntercomEvent(event, step) {
  const experienceId = yield select(selectCurrentExperienceId);
  const experience = yield select(selectExperienceById(experienceId));
  const properties = yield getExperiencePropertiesForIntercom(experience);
  const propertiesWithStep = { ...properties, step };
  const date = yield select(selectBookingDate);

  if (event === IntercomEvents.CheckoutGuests) {
    const merged = decamelizeKeys({
      ...propertiesWithStep,
      date,
    });

    Intercom.logEvent(event, merged);
    return;
  }

  if (event === IntercomEvents.CheckoutPayment || event === IntercomEvents.PurchaseComplete) {
    const totalPrice = yield select(selectBookingPrice);
    const tickets = yield select(selectBookingTickets);
    const selectedTicketsCount = yield select(selectSelectedTicketsCount);

    const merged = decamelizeKeys({
      ...propertiesWithStep,
      date,
      adults: tickets[TICKET_TYPES.Adult],
      children: tickets[TICKET_TYPES.Child],
      totalGuests: selectedTicketsCount,
      totalPrice: getIntercomPriceObject(totalPrice),
    });

    Intercom.logEvent(event, merged);
    return;
  }

  Intercom.logEvent(event, propertiesWithStep);
}

const intercomEventHandlersMap = {
  [IntercomEvents.ExperienceView]: handleIntercomExperienceViewEvent,
  [IntercomEvents.CheckoutDatesAndTimes]: () =>
    handleCheckoutStepIntercomEvent(IntercomEvents.CheckoutDatesAndTimes, IntercomBookingStep.DateAndTime),
  [IntercomEvents.CheckoutAuthenticate]: () =>
    handleCheckoutStepIntercomEvent(IntercomEvents.CheckoutAuthenticate, IntercomBookingStep.Authenticate),
  [IntercomEvents.CheckoutGuests]: () =>
    handleCheckoutStepIntercomEvent(IntercomEvents.CheckoutGuests, IntercomBookingStep.Guests),
  [IntercomEvents.CheckoutPayment]: () =>
    handleCheckoutStepIntercomEvent(IntercomEvents.CheckoutPayment, IntercomBookingStep.Payment),
  [IntercomEvents.PurchaseComplete]: () => handleCheckoutStepIntercomEvent(IntercomEvents.PurchaseComplete),
};

function* logIntercomEvent({ event, params }) {
  const handler = intercomEventHandlersMap[event];

  if (handler) {
    yield handler(params);
  }
}

export default function* startupSaga() {
  yield all([
    takeEvery(AnalyticsTypes.LOG_GTM_EVENT, logGTMEvent),
    takeEvery(AnalyticsTypes.LOG_INTERCOM_EVENT, logIntercomEvent),
    takeEvery(AnalyticsTypes.LOG_FILTERED_VIEWED_EXPERIENCES, logFilteredViewedExperiences),
    takeEvery(AnalyticsTypes.LOG_MARKET_VIEWED_EXPERIENCES, logMarketViewedExperiences),
    takeEvery(AnalyticsTypes.LOG_KIOSK_VIEWED_EXPERIENCES, logKioskViewedExperiences),
  ]);
}
